import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api, { LIMIT_EVENT } from '../api/api';
import { browserLocale, readCached, writeCached } from './format';
import UpgradeModal from './UpgradeModal';

// The signed-in user's plan, usage and the pricing catalog, plus the upgrade
// prompt. Any request that hits a plan limit (402) opens the prompt.
const BillingContext = createContext(null);

export const useBilling = () => useContext(BillingContext);

// Only answers with the expected shape are used or saved: older builds could
// cache a web page here when the API address was wrong
const isPricing = (data) => Array.isArray(data?.plans);
const isBilling = (data) => Boolean(data) && typeof data === 'object' && !Array.isArray(data);

// Read once per key, so the cached catalog keeps its identity between renders
const pricingCache = {};
const readCachedPricing = (key) => {
    if (!(key in pricingCache)) {
        const cached = readCached(key);
        pricingCache[key] = isPricing(cached) ? cached : null;
    }
    return pricingCache[key];
};

// Sends the browser to a Stripe-hosted page
const goTo = (url) => {
    window.location.assign(url);
};

export const BillingProvider = ({ user, children }) => {
    const userId = user?.id;
    // Billing is kept with its user, starting from the last answer saved for them
    const [billingState, setBillingState] = useState({ userId: '', data: null, isSettled: false });
    const cachedBilling = useMemo(() => {
        const cached = userId ? readCached(`billing:${userId}`) : null;
        return isBilling(cached) ? cached : null;
    }, [userId]);
    const billing = userId ? (billingState.userId === userId && billingState.data) || cachedBilling : null;
    // False only while the first answer for this user is on its way and nothing is saved to show
    const isLoaded = !userId || Boolean(billing) || (billingState.userId === userId && billingState.isSettled);
    const setBilling = useCallback((next) => {
        setBillingState(prev => {
            const previous = (prev.userId === userId && prev.data) || cachedBilling;
            return { userId, data: typeof next === 'function' ? next(previous) : next, isSettled: true };
        });
    }, [userId, cachedBilling]);
    const [freshPricing, setPricing] = useState(null);
    const [upgrade, setUpgrade] = useState(null);
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');

    const refresh = useCallback(async () => {
        if (!userId) return null;
        try {
            const data = await api.getBilling();
            if (!isBilling(data)) throw new Error('Unexpected billing response');
            setBilling(data);
            writeCached(`billing:${userId}`, data);
            return data;
        } catch (e) {
            console.error('Error loading billing:', e);
            setBillingState(prev => (prev.userId === userId ? { ...prev, isSettled: true } : { userId, data: null, isSettled: true }));
            return null;
        }
    }, [userId, setBilling]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Prices are in US dollars; Stripe Checkout offers each customer their own
    // currency (Adaptive Pricing). Subscribers already billed in another
    // currency keep seeing prices in it.
    const currency = billing?.account?.currency || 'usd';
    const pricingKey = `pricing:${currency || 'auto'}`;
    const pricing = (freshPricing?.key === pricingKey && freshPricing.data) || readCachedPricing(pricingKey);
    useEffect(() => {
        let cancelled = false;
        api.getPricing({ currency, locale: browserLocale() })
            .then(data => {
                if (!isPricing(data)) throw new Error('Unexpected pricing response');
                writeCached(pricingKey, data);
                if (!cancelled) setPricing({ key: pricingKey, data });
            })
            .catch(e => console.error('Error loading pricing:', e));
        return () => { cancelled = true; };
    }, [currency, pricingKey]);

    useEffect(() => {
        const handleLimit = (event) => {
            setUpgrade({ ...event.detail, at: Date.now() });
            refresh();
        };
        window.addEventListener(LIMIT_EVENT, handleLimit);
        return () => window.removeEventListener(LIMIT_EVENT, handleLimit);
    }, [refresh]);

    const run = useCallback(async (key, action) => {
        setBusy(key);
        setError('');
        try {
            return await action();
        } catch (e) {
            setError(e.response?.data?.error || "Something went wrong. Please try again.");
            return null;
        } finally {
            setBusy('');
        }
    }, []);

    const clearError = useCallback(() => setError(''), []);

    const account = billing?.account;
    const returnPath = () => (window.location.pathname.startsWith('/dashboard') ? window.location.pathname : '/dashboard/billing');

    // New subscribers go to Stripe Checkout; existing ones switch in place
    const choosePlan = useCallback((plan, interval) => run(`plan:${plan}`, async () => {
        if (account?.subscribedPlan) {
            const result = await api.changePlan({ plan, interval });
            setBilling(prev => ({ ...prev, account: result.account }));
            await refresh();
            setUpgrade(null);
            return result;
        }
        const { url } = await api.startCheckout({ plan, interval, currency: pricing?.currency, returnPath: returnPath() });
        goTo(url);
        return null;
    }), [account?.subscribedPlan, pricing?.currency, refresh, run, setBilling]);

    const buyPack = useCallback((pack) => run(`pack:${pack}`, async () => {
        const { url } = await api.buyPack({ pack, currency: pricing?.currency, returnPath: returnPath() });
        goTo(url);
    }), [pricing?.currency, run]);

    const openPortal = useCallback((flow) => run('portal', async () => {
        const { url } = await api.openBillingPortal(flow);
        goTo(url);
    }), [run]);

    // Plan changes made in the app (offers, cancel, resume) return the new account
    const applyAccount = useCallback((next) => {
        setBilling(prev => (prev ? { ...prev, account: next } : prev));
        refresh();
    }, [refresh, setBilling]);

    const value = useMemo(() => ({
        billing,
        isLoaded,
        account,
        usage: billing?.usage,
        billingEnabled: billing?.billingEnabled ?? false,
        pricing,
        currency: pricing?.currency || currency,
        refresh,
        applyAccount,
        choosePlan,
        buyPack,
        openPortal,
        busy,
        error,
        clearError,
        // reason: a limit detail from the API, or { metric: 'manual' } from an Upgrade button
        openUpgrade: (reason = { metric: 'manual' }) => setUpgrade({ ...reason, at: Date.now() }),
    }), [billing, isLoaded, account, pricing, currency, refresh, applyAccount, choosePlan, buyPack, openPortal, busy, error, clearError]);

    return (
        <BillingContext.Provider value={value}>
            {children}
            {upgrade && user && <UpgradeModal reason={upgrade} onClose={() => { setUpgrade(null); clearError(); }} />}
        </BillingContext.Provider>
    );
};
